/**
 * Y-axis domain/ticks for trading PnL comparison charts.
 *
 * Recharts' default nice-scale treats zero as a tick and allocates a *full*
 * positive step below zero. A −$50 dip then expands to −$700 of empty space.
 * We nice the positive side normally and only open as much negative room as
 * the data needs, capped below one positive tick gap when possible.
 * Large drawdowns get the same step below zero (not a single unlabeled floor).
 */

export type TradingPnlYAxis = {
  domain: [number, number];
  ticks: number[];
};

function startOfLocalDayMs(timeMs: number): number {
  const d = new Date(timeMs);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function calendarDayIndex(timeMs: number): number {
  const d = new Date(startOfLocalDayMs(timeMs));
  return Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000);
}

function dateFromCalendarDayIndex(dayIndex: number): number {
  const utc = new Date(dayIndex * 86_400_000);
  return new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate()).getTime();
}

/**
 * Evenly spaced X ticks from first→last day (inclusive).
 * Avoids Recharts' "nice" time ticks that look weekly then jump to the end date.
 */
export function computeEvenDayAxisTicks(
  startMs: number,
  endMs: number,
  tickCount = 6
): number[] {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return [];
  const start = startOfLocalDayMs(startMs);
  const end = startOfLocalDayMs(endMs);
  if (end <= start) return [start];

  const startDay = calendarDayIndex(start);
  const endDay = calendarDayIndex(end);
  const span = endDay - startDay;
  const count = Math.max(2, Math.min(tickCount, span + 1));
  const ticks: number[] = [];
  for (let i = 0; i < count; i++) {
    const day = startDay + Math.round((i * span) / (count - 1));
    ticks.push(dateFromCalendarDayIndex(day));
  }
  // De-dupe while preserving order (short ranges can collide).
  return ticks.filter((t, i) => i === 0 || t !== ticks[i - 1]);
}

function niceNum(range: number, round: boolean): number {
  if (!(range > 0) || !Number.isFinite(range)) return 1;
  const exp = Math.floor(Math.log10(range));
  const magnitude = 10 ** exp;
  const frac = range / magnitude;
  let niceFrac: number;
  if (round) {
    if (frac < 1.5) niceFrac = 1;
    else if (frac < 3) niceFrac = 2;
    else if (frac < 7) niceFrac = 5;
    else niceFrac = 10;
  } else if (frac <= 1) niceFrac = 1;
  else if (frac <= 2) niceFrac = 2;
  else if (frac <= 5) niceFrac = 5;
  else niceFrac = 10;
  return niceFrac * magnitude;
}

function nicePositiveTicks(max: number, tickCount: number): number[] {
  const top = Math.max(0, max);
  if (top === 0) return [0];
  const gaps = Math.max(2, tickCount - 1);
  const step = niceNum(top / gaps, true);
  const niceMax = Math.ceil(top / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= niceMax + step * 1e-9; v += step) {
    ticks.push(Number(v.toPrecision(12)));
  }
  return ticks;
}

function collectFinite(values: readonly number[]): { min: number; max: number } | null {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < values.length; i++) {
    const v = Number(values[i]);
    if (!Number.isFinite(v)) continue;
    min = Math.min(min, v);
    max = Math.max(max, v);
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { min, max };
}

/** Negative tick: cover dataMin, stay under one positive gap when the dip is small. */
function tightNegativeFloor(dataMin: number, positiveStep: number): number {
  const absMin = Math.abs(dataMin);
  const softCap = positiveStep * 0.85;
  if (!(absMin > 0)) return 0;

  if (absMin <= softCap) {
    const step = niceNum(Math.max(absMin * 1.1, positiveStep * 0.05), true);
    let tick = Math.ceil(absMin / step) * step;
    if (tick < absMin) tick = absMin;
    tick = Math.min(tick, softCap);
    if (tick < absMin) tick = absMin; // never clip real data
    return -tick;
  }

  // Real drawdown larger than one positive gap: follow data with light pad only.
  const pad = Math.max(absMin * 0.05, positiveStep * 0.05);
  return dataMin - pad;
}

/**
 * Negative ticks for a large drawdown — same step as the positive side, domain
 * floored to that step so labels are even (−300, −150, 0, …) instead of a lone floor.
 */
function negativeTicksForLargeDrawdown(negFloor: number, positiveStep: number): {
  domainMin: number;
  ticks: number[];
} {
  if (!(negFloor < 0) || !(positiveStep > 0)) {
    return { domainMin: negFloor, ticks: negFloor < 0 ? [negFloor] : [] };
  }
  let domainMin = Math.floor(negFloor / positiveStep) * positiveStep;
  if (domainMin > negFloor) domainMin = negFloor;
  const ticks: number[] = [];
  for (let v = domainMin; v < -positiveStep * 1e-9; v += positiveStep) {
    ticks.push(Number(v.toPrecision(12)));
  }
  return { domainMin, ticks };
}

export function computeTradingPnlYAxis(
  values: readonly number[],
  tickCount = 5
): TradingPnlYAxis {
  const extent = collectFinite(values);
  if (!extent) return { domain: [0, 1], ticks: [0, 1] };

  const { min: dataMin, max: dataMax } = extent;
  const top = Math.max(0, dataMax);

  if (dataMin >= 0) {
    const ticks = nicePositiveTicks(top, tickCount);
    const domainMax = ticks[ticks.length - 1] ?? Math.max(top, 1);
    return { domain: [0, domainMax], ticks };
  }

  if (top <= 0) {
    // All non-positive: mirror the positive logic below zero.
    const ticks = nicePositiveTicks(Math.abs(dataMin), tickCount).map((t) => -t).reverse();
    const domainMin = ticks[0] ?? dataMin;
    return { domain: [domainMin, 0], ticks };
  }

  const positiveTicks = nicePositiveTicks(top, tickCount);
  const domainMax = positiveTicks[positiveTicks.length - 1] ?? top;
  const positiveStep =
    positiveTicks.length >= 2 ? positiveTicks[1]! - positiveTicks[0]! : domainMax;

  const negFloor = tightNegativeFloor(dataMin, positiveStep);
  const softCap = positiveStep * 0.85;

  // Small dip: single tight negative tick (avoid a full empty positive-sized band).
  if (Math.abs(negFloor) <= softCap + 1e-9) {
    const ticks = [negFloor, ...positiveTicks.filter((t) => t >= 0)];
    return { domain: [negFloor, domainMax], ticks };
  }

  // Large drawdown: fill intermediate labels with the same step as above zero.
  const { domainMin, ticks: negTicks } = negativeTicksForLargeDrawdown(negFloor, positiveStep);
  const ticks = [...negTicks, ...positiveTicks.filter((t) => t >= 0)];
  return { domain: [domainMin, domainMax], ticks };
}

export function seriesValuesFromChartRows(
  rows: Array<Record<string, number | string>>,
  seriesIds: string[]
): number[] {
  const out: number[] = [];
  for (const row of rows) {
    for (const id of seriesIds) {
      const v = Number(row[id]);
      if (Number.isFinite(v)) out.push(v);
    }
  }
  return out;
}
